import React, { useEffect, useContext, useState } from 'react';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { hot } from 'react-hot-loader';
import { Loader } from '@ferris/react/components/loader';
import { Icon } from '@ferris/react/components/icon';
import { TrackingContext } from '@vrbo/react-event-tracking';
import withPageFlow from '../../PageFlow/PageFlow';
import TranslationUtil from '../../../utils/TranslationUtil';
import Disclaimer from '../Disclaimer';
import AppActions from '../../../actions/app';
import EstimateCta from './EstimateCta';
import { formatWithLocalCurrency } from '../../../utils/CurrencyUtil/index';
import { capitalizeBrand } from '../../../utils/BrandUtil';
import ExperimentUtil from '../../../utils/ExperimentUtil';
import { createPageViewEvent } from '../../../utils/EventProviders/EgClickstreamUtil';
import {
    isEnUsPermanentFeatureVrEnabled
} from '../../../utils/ExperimentsUtil';
import { ESTIMATE_PAGE } from '../../PageFlow/PageList';
import PropertyCreationErrorBanner from '../../VrPropertyCreation/PropertyCreationErrorBanner';

export function EstimateResults(props) {
    const {
        estimateInfo,
        estimateInfoFallback,
        annualEstimate,
        monthlyEstimate,
        estimateType,
        estimateStart,
        getEstimate,
        lat,
        lng,
        hydrationComplete,
        locale,
        estimateLoading,
        estimateFailed,
        bedroomCount,
        bathroomCount,
        failedEstimateText,
        toggleModalOpen,
        isFallback,
        setCreatePropertyError,
        fallbackSource
    } = props;

    const [isLoaded, setIsLoaded] = useState(false);
    const { trigger } = useContext(TrackingContext);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (trigger) { // we have to do this for the unit tests to work.
            trigger('pageLoad', { pageName: 'partner-qualifier: EstimateResults' });
            trigger('pageview', createPageViewEvent('Estimateresults', '', '1.4.0'));
        }
        setTimeout(() => { setIsLoaded(true); }, 150);
        return () => {
            setCreatePropertyError(null);
            trigger('pageUnload');
        };
    }, []);

    const handleModalOpen = () => {
        trigger('genericClick', {
            category: 'navigation',
            action: 'EstimateResults: openModal'
        });
        toggleModalOpen(true);
    };

    useEffect(() => {
        if (hydrationComplete) getEstimate(lat, lng, bedroomCount, bathroomCount, locale);
    }, [hydrationComplete]);

    const renderEstimateInfo = () => (
        <div className="ulx-estimate__note" data-wdio="estimate-note">
            {isFallback && fallbackSource === 'adi' ? estimateInfoFallback : estimateInfo}
        </div>
    );

    const renderEstimate = () => {
        const estimateTextWithoutIcon = estimateType === 'monthly' ? monthlyEstimate : annualEstimate;
        const lastSpanIndex = estimateTextWithoutIcon.lastIndexOf('</span>');
        const estimateText = [
            estimateTextWithoutIcon.substring(0, lastSpanIndex),
            '<span id="estimateInfoIcon">',
            estimateTextWithoutIcon.substring(lastSpanIndex)
        ].join('');

        const options = {
            replace: ({ attribs }) => {
                if (!attribs) return;
                if (attribs.id === 'estimateInfoIcon') {
                    // eslint-disable-next-line consistent-return
                    return (
                        <span id="estimateInfoIcon">
                            <button type="button" className="estimate-info-icon" onClick={handleModalOpen} aria-label="More information">
                                <Icon name="info-outline" color="interactive" size="medium" />
                            </button>
                        </span>
                    );
                }
            }
        };

        const htmlElement = parse(estimateText, options);
        return (
            <>
                <div className="ulx-estimate__title is-good">
                    <h2>
                        {htmlElement}
                    </h2>
                </div>
                {renderEstimateInfo()}
                <hr />
                <div className="ulx-estimate__lyp">
                    <h3>
                        {parse(estimateStart)}
                    </h3>
                </div>
                <EstimateCta {...props} />
                <Disclaimer {...props} />
            </>
        );
    };

    const renderFailedEstimate = () => (
        <>
            <div className="ulx-estimate__content">
                <h2>{failedEstimateText}</h2>
            </div>
            <EstimateCta {...props} />
        </>
    );

    return (
        <div className="ulx-estimate__outer" data-wdio="estimate-content">
            {(estimateLoading || !isLoaded) && (
                <Loader
                    inline
                    size="mini"
                    visible
                />
            )}
            <PropertyCreationErrorBanner />
            {isLoaded && !estimateLoading && !estimateFailed && renderEstimate()}
            {isLoaded && !estimateLoading && estimateFailed && renderFailedEstimate()}
        </div>
    );
}

EstimateResults.propTypes = {
    headerText: PropTypes.string.isRequired,
    propertyDetailsText: PropTypes.string.isRequired,
    propertyDetailsTextMobile: PropTypes.string.isRequired,
    editText: PropTypes.string.isRequired,
    estimateInfo: PropTypes.string.isRequired,
    estimateInfoFallback: PropTypes.string.isRequired,
    annualEstimate: PropTypes.string.isRequired,
    monthlyEstimate: PropTypes.string.isRequired,
    estimateType: PropTypes.string,
    estimateStart: PropTypes.string.isRequired,
    estimateButtonLabel: PropTypes.string.isRequired,
    getEstimate: PropTypes.func.isRequired,
    lat: PropTypes.number,
    lng: PropTypes.number,
    hydrationComplete: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    bedroomCount: PropTypes.number.isRequired,
    bathroomCount: PropTypes.number.isRequired,
    estimateLoading: PropTypes.bool.isRequired,
    estimateFailed: PropTypes.bool.isRequired,
    failedEstimateText: PropTypes.string.isRequired,
    disclaimerText: PropTypes.string.isRequired,
    disclaimerTitle: PropTypes.string.isRequired,
    isModalOpen: PropTypes.bool.isRequired,
    toggleModalOpen: PropTypes.func.isRequired,
    disclaimerLines: PropTypes.arrayOf(PropTypes.string).isRequired,
    isFallback: PropTypes.bool.isRequired,
    pageFlow: PropTypes.shape({
        next: PropTypes.func
    }).isRequired,
    setCreatePropertyError: PropTypes.func.isRequired,
    genericCreatePropertyErrorMessage: PropTypes.string.isRequired,
    createdPropertyTriad: PropTypes.string,
    createPropertyError: PropTypes.string,
    fallbackSource: PropTypes.string.isRequired,
    competitorLink: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        fieldLabel: PropTypes.string,
        display: PropTypes.bool.isRequired
    }).isRequired
};

EstimateResults.defaultProps = {
    createdPropertyTriad: undefined,
    createPropertyError: undefined,
    hydrationComplete: false,
    estimateType: undefined, // Set after page load via gql call
    lat: undefined, // Set after page load via local storage
    lng: undefined // Set after page load via local storage
};

/* istanbul ignore next */
export const mapStateToProps = (state) => {
    const {
        app,
        hydration,
        location,
        lead
    } = state;

    const translations = new TranslationUtil(app.translations, app.fallbackTranslations);
    const getTranslation = (suffix, params) => translations.getTranslation(`lyp.estimate.${suffix}`, params);
    const { bedroomCount, bathroomCount } = app.lyp;
    const { hydrationComplete } = hydration;
    const { lat, lng } = location.latLng;
    const {
        estimate, getEstimate, createdPropertyTriad, brand
    } = app;
    const brandName = capitalizeBrand(brand);
    const estimateAmount = estimate ? estimate.estimateUpperBound : 0;
    // Null check if we don't have the estimate yet
    const { estimateType, currencyCode } = estimate || {};
    const estimateLoading = !getEstimate.success && !getEstimate.fail;
    const estimateFailed = getEstimate.fail;
    const filterTranslations = (translationsMap) => Object.keys(translationsMap).filter((key) => key.includes('estimate.disclaimerline'));
    const fallbackBedroomCount = app.estimate ? app.estimate.beds : bedroomCount;
    let disclaimerLines = filterTranslations(app.translations);
    // if the estimate translations don't exist in the locale, use the fallback ones
    disclaimerLines = disclaimerLines.length
        ? disclaimerLines : filterTranslations(app.fallbackTranslations);
    const locale = app.locale.canonical;
    const currencyDisclaimer = 'Vrbo.lyp.estimate.currencyConversion';
    const experimentUtil = new ExperimentUtil(app.experiments);
    const contentMessagingUpdatesEnabled = isEnUsPermanentFeatureVrEnabled(experimentUtil);
    const competitorLink = {
        title: translations.getTranslation('Vrbo.lyp.estimate.competitor.link.title'),
        description: translations.getTranslation('Vrbo.lyp.estimate.competitor.link.description'),
        fieldLabel: translations.getTranslation('Vrbo.lyp.estimate.competitor.link.fieldLabel'),
        display: true
    };

    const estimateStartKey = 'Vrbo.lyp.estimate.annualEstimateStartTest';
    return {
        competitorLink,
        headerText: getTranslation('header'),
        propertyDetailsText: getTranslation('details', { bedroomCount, bathroomCount }),
        propertyDetailsTextMobile: getTranslation('detailsMobile'),
        editText: getTranslation('edit'),
        estimateInfo: translations.getTranslation(contentMessagingUpdatesEnabled ? 'Vrbo.lyp.estimate.estimateInfoTest' : 'Vrbo.lyp.estimate.estimateInfo'),
        estimateInfoFallback: translations.getTranslation(contentMessagingUpdatesEnabled ? 'Vrbo.lyp.estimate.estimateInfoFallbackTest' : 'Vrbo.lyp.estimate.estimateInfoFallback', { bedroomCount: fallbackBedroomCount, brandName }),
        annualEstimate: translations.getTranslation(contentMessagingUpdatesEnabled ? 'Vrbo.lyp.estimate.annualEstimateTest' : 'Vrbo.lyp.estimate.annualEstimate', { estimate: formatWithLocalCurrency(estimateAmount, locale, currencyCode) }),
        monthlyEstimate: translations.getTranslation(contentMessagingUpdatesEnabled ? 'Vrbo.lyp.estimate.monthlyEstimateTest' : 'Vrbo.lyp.estimate.monthlyEstimate', { estimate: formatWithLocalCurrency(estimateAmount, locale, currencyCode) }),
        estimateType,
        estimateStart: translations.getTranslation(contentMessagingUpdatesEnabled ? estimateStartKey : 'Vrbo.lyp.estimate.annualEstimateStart'), // Not actually specific to annual estimates
        estimateButtonLabel: translations.getTranslation('Vrbo.cta.next'),
        translationUtil: translations,
        bedroomCount,
        bathroomCount,
        lat,
        lng,
        hydrationComplete,
        locale,
        estimateLoading,
        estimateFailed,
        brandName,
        failedEstimateText: translations.getTranslation('Vrbo.lyp.estimate.failed'),
        isModalOpen: app.modalOpen,
        disclaimerTitle: translations.getTranslation('Vrbo.lyp.estimate.disclaimerTitle'),
        disclaimerText: getTranslation('disclaimer'),
        closeButtonText: translations.getTranslation('Vrbo.lyp.estimate.close'),
        disclaimerLines,
        currencyDisclaimer,
        isFallback: app.estimate ? app.estimate.isFallback : false,
        fallbackSource: app.estimate ? app.estimate.fallbackSource : '',
        conversionRate: app.estimate ? `${formatWithLocalCurrency(app.estimate.conversionRate, locale, currencyCode, 3)}` : '',
        targetCurrency: app.estimate ? app.estimate.currencyCode : '',
        genericCreatePropertyErrorMessage: translations.getTranslation('Vrbo.lyp.estimate.property.creation.error.message'),
        createdPropertyTriad,
        newSupplier: lead.newSupplier,
        siteName: app.siteName
    };
};

/* istanbul ignore next */
export const mapDispatchToProps = (dispatch) => ({
    getEstimate: (
        lat,
        lng,
        bedroomNum,
        bathroomNum,
        locale
    ) => dispatch(AppActions.getEstimate({
        lat, lng, bedroomNum, bathroomNum
    }, locale)),
    toggleModalOpen: (modalOpen) => dispatch(AppActions.toggleModalOpen(modalOpen)),
    setCreatePropertyError: (error) => dispatch(AppActions.setCreatePropertyError(error))
});

const ConnectedEstimateResults = connect(mapStateToProps, mapDispatchToProps)(EstimateResults);
ConnectedEstimateResults.displayName = ESTIMATE_PAGE;
export default hot(module)(withPageFlow(ConnectedEstimateResults));
